import { ApActionStatus } from '../enums/ApActionStatus';
import { ApActionType } from '../enums/ApActionType';

export type ApPlanActionData = {
    actionId?: number;
    subject?: string;
    body?: string;
    mediaUrls?: string[];
    uuid?: string;
};

export type ApPlanAction = {
    actionId: number;
    clientId: number;
    contactId: number;
    planId?: number;
    parentId?: number;
    templateActionId?: number;
    status: ApActionStatus;
    lastStatusDate?: Date;
    lastStatusMessage: string;
    type: ApActionType;
    assignedUser: string;
    scheduledDate: Date;
    title: string;
    data: ApPlanActionData;
    actions: ApPlanAction[];
};

export interface CreateApPlanActionRequest {
    contactId: number;
    parentId?: number;
    templateActionId?: number;
    type: ApActionType;
    assignedUser: string;
    scheduledDate: Date;
    title: string;
    data: Omit<ApPlanActionData, 'actionId'>;
}

/**
 * buildCreateApPlanAction
 *
 * @description
 * Builds an Ap plan action object which matches the object expected by the server
 * in a request to create a new ap plan action.
 *
 * @hidden
 * Adds default values for object properties which are optional and not passed in the argument object
 */
export function buildCreateApPlanAction(args: {
    title?: string;
    type: ApActionType;
    contactId: number;
    parentId?: number;
    assignedUser: string;
    scheduledDate?: Date;
    templateActionId?: number;
    subject?: string;
    body?: string;
}): CreateApPlanActionRequest {
    const currentDate = new Date();

    return {
        title: `New ${args.type} Action - ${new Date().toLocaleDateString()}`,
        type: args.type,
        parentId: args.parentId,
        contactId: args.contactId,
        assignedUser: args.assignedUser,
        scheduledDate: args.scheduledDate || currentDate,
        templateActionId: args.templateActionId,
        data: {
            body: args.body || '',
            subject: args.subject || '',
            mediaUrls: [],
        },
    };
}

export interface UpdateApPlanActionRequest {
    type: ApActionType;
    assignedUser: string;
    title: string;
    data: ApPlanActionData;
}

/**
 * buildUpdateApPlanAction
 *
 * @description
 * Builds an object from an existing Ap plan action which matches what is expected by the
 * server in a request to update an Ap plan action
 */
export function buildUpdateApPlanAction(args: {
    /*
        property actionId is not required by the server but makes merging into a full ApAction
        object easier if it exists in the action's "data" object
     */
    actionId: number;
    type: ApActionType;
    assignedUser: string;
    title: string;
    body: string;
    subject?: string;
    mediaUrls?: string[];
}): UpdateApPlanActionRequest {
    return {
        assignedUser: args.assignedUser,
        data: {
            actionId: args.actionId,
            subject: args.subject || '',
            body: args.body,
            mediaUrls: args.mediaUrls || [],
        },
        title: args.title,
        type: args.type,
    };
}

export type CreateDirectApPlanActionRequest = {
    title: string;
    actionType: ApActionType;
    contactId: number;
    clientFieldId: number;
    assignedUser?: string;
    data: ApPlanActionData;
};

/**
 * buildCreateDirectApPlanAction
 *
 * @description
 * Builds an Ap plan action object which matches the object expected by the server
 * in a request to create a new direct ap plan action.
 *
 * @hidden
 * Adds default values for object properties which are optional and not passed in the argument object
 */
export function buildCreateDirectApPlanAction(args: {
    title?: string;
    type: ApActionType;
    contactId: number;
    clientFieldId: number;
    assignedUser: string;
    data: ApPlanActionData;
}): CreateDirectApPlanActionRequest {
    return {
        title: `New ${args.type} Action - ${new Date().toLocaleDateString()}`,
        actionType: args.type,
        contactId: args.contactId,
        clientFieldId: args.clientFieldId,
        assignedUser: args.assignedUser,
        data: args.data,
    };
}
